import { useState } from 'react';
import { useTranslate } from '@refinedev/core';
import { Breadcrumb, List } from '@refinedev/antd';
import { Col, Row, Space, Tabs } from 'antd';
import {
  BankOutlined,
  DollarOutlined,
  LikeOutlined,
  ReadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { UserKpiCards } from './dashboard/user/UserKpiCards';
import { RatingDistribution } from './dashboard/RatingDistribution';
import { RegistrationTrend } from './dashboard/user/RegistrationTrend';
import { ReviewKpiCards } from './dashboard/ReviewKpiCards';
import { ReviewTrend } from './dashboard/ReviewTrend';
import { TokenActivity } from './dashboard/user/TokenActivity';
import { UserComposition } from './dashboard/user/UserComposition';
import { LiveActivityCard } from './dashboard/progress/LiveActivityCard';
import { ProgressKpiCards } from './dashboard/progress/ProgressKpiCards';
import { DailyReadersChart } from './dashboard/progress/DailyReadersChart';
import { ProgressionDistribution } from './dashboard/progress/ProgressionDistribution';
import { TopPublicationsTable } from './dashboard/progress/TopPublicationsTable';
import { ListeningHeatmapCard } from './dashboard/progress/ListeningHeatmapCard';
import { SegmentHealthCards } from './dashboard/progress/SegmentHealthCards';
import { SettlementLiveBar } from './dashboard/settlement/SettlementLiveBar';
import { SettlementFiltersBar } from './dashboard/settlement/SettlementFiltersBar';
import { SettlementKpiCards } from './dashboard/settlement/SettlementKpiCards';
import { SettlementAccrualCard } from './dashboard/settlement/SettlementAccrualCard';
import { SettlementRevenueTrendCard } from './dashboard/settlement/SettlementRevenueTrendCard';
import { SettlementRankingsCard } from './dashboard/settlement/SettlementRankingsCard';
import { SettlementSystemHealthCard } from './dashboard/settlement/SettlementSystemHealthCard';
import type { SettlementStatsFilters } from './dashboard/settlement/filters';
import { MonetizationTabContent } from './dashboard/monetization/MonetizationTabContent';

export const DashboardPage = () => {
  const t = useTranslate();
  const [settlementFilters, setSettlementFilters] =
    useState<SettlementStatsFilters>({});

  return (
    <List
      canCreate={false}
      title={t('dashboard.title')}
      // The dashboard is its own root, so its trail is a single crumb — below
      // the default `minItems`, and the home link would repeat it.
      breadcrumb={<Breadcrumb minItems={1} showHome={false} />}
    >
      <Tabs
        defaultActiveKey="iam"
        items={[
          {
            key: 'iam',
            label: (
              <Space size={6}>
                <UserOutlined />
                Użytkownicy
              </Space>
            ),
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <UserKpiCards />
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={12}>
                    <RegistrationTrend />
                  </Col>
                  <Col xs={24} lg={12}>
                    <TokenActivity />
                  </Col>
                </Row>
                <UserComposition />
              </Space>
            ),
          },
          {
            key: 'progress',
            label: (
              <Space size={6}>
                <ReadOutlined />
                Konsumpcja
              </Space>
            ),
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <LiveActivityCard />
                <ProgressKpiCards />
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <DailyReadersChart />
                  </Col>
                  <Col xs={24} lg={10}>
                    <ProgressionDistribution />
                  </Col>
                </Row>
                <TopPublicationsTable />
                <ListeningHeatmapCard />
                <SegmentHealthCards />
              </Space>
            ),
          },
          {
            key: 'settlement',
            label: (
              <Space size={6}>
                <BankOutlined />
                Rozliczenia
              </Space>
            ),
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <SettlementLiveBar />
                <SettlementFiltersBar
                  value={settlementFilters}
                  onChange={setSettlementFilters}
                />
                <SettlementKpiCards filters={settlementFilters} />
                <SettlementAccrualCard filters={settlementFilters} />
                <SettlementRevenueTrendCard filters={settlementFilters} />
                <SettlementRankingsCard filters={settlementFilters} />
                <SettlementSystemHealthCard />
              </Space>
            ),
          },
          {
            key: 'monetization',
            label: (
              <Space size={6}>
                <DollarOutlined />
                Monetyzacja
              </Space>
            ),
            children: <MonetizationTabContent />,
          },
          {
            key: 'feedback',
            label: (
              <Space size={6}>
                <LikeOutlined />
                Recenzje
              </Space>
            ),
            children: (
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <ReviewKpiCards />
                <Row gutter={[16, 16]}>
                  <Col xs={24} lg={14}>
                    <ReviewTrend />
                  </Col>
                  <Col xs={24} lg={10}>
                    <RatingDistribution />
                  </Col>
                </Row>
              </Space>
            ),
          },
        ]}
      />
    </List>
  );
};
